/**
 * Gamified celebration — vanilla canvas confetti, no dependency.
 *
 * The dashboard is a gamification product, so saving should FEEL like a small
 * win, not flash a system "Saved" toast (which Victor hates). Three intensities:
 *   • addWidget  — a burst where the card lands, plus a card pop-in class
 *   • publish    — the biggest burst, the moment the board goes live
 *   • (edit save is handled in the editor as a button flip, no confetti)
 *
 * One shared full-screen canvas is created on demand, drawn on with
 * requestAnimationFrame, and removed when the last particle falls. It sits at a
 * very high z-index with pointer-events:none so it never intercepts clicks.
 */

const COLORS = ["#1B598E", "#F5B301", "#1D9E75", "#F0997B", "#7F77DD", "#3B82C4", "#ffffff"];

let canvas = null;
let ctx = null;
let particles = [];
let raf = 0;

const resize = () => {
  if (!canvas) return;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
};

const ensureCanvas = () => {
  if (canvas) return;
  canvas = document.createElement("canvas");
  canvas.setAttribute("aria-hidden", "true");
  const s = canvas.style;
  s.position = "fixed";
  s.inset = "0";
  s.width = "100%";
  s.height = "100%";
  s.pointerEvents = "none";
  s.zIndex = "2147483000"; // above the drawer + grid, below nothing that matters
  document.body.appendChild(canvas);
  ctx = canvas.getContext("2d");
  resize();
};

const teardown = () => {
  cancelAnimationFrame(raf);
  raf = 0;
  particles = [];
  if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
  canvas = null;
  ctx = null;
};

const tick = () => {
  if (!ctx) return;
  ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
  let alive = 0;
  particles.forEach((p) => {
    if (p.life <= 0) return;
    alive += 1;
    p.vy += 0.16; // gravity
    p.vx *= 0.99;
    p.x += p.vx;
    p.y += p.vy;
    p.rot += p.spin;
    p.life -= 1;
    const fade = Math.min(1, p.life / 26);
    ctx.save();
    ctx.globalAlpha = fade;
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.fillStyle = p.color;
    // little rectangles — reads as paper confetti, cheap to draw
    ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.62);
    ctx.restore();
  });
  if (alive === 0) {
    teardown();
    return;
  }
  raf = requestAnimationFrame(tick);
};

/**
 * Emit `count` particles from a fractional screen origin { x, y } in 0..1.
 * spread controls the cone (radians), power the initial speed.
 */
const emit = ({ x = 0.5, y = 0.45, count = 90, spread = Math.PI, power = 9, up = true } = {}) => {
  // Honour reduced-motion — a single soft pulse instead of a shower.
  const reduce =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  ensureCanvas();
  const ox = x * window.innerWidth;
  const oy = y * window.innerHeight;
  const n = reduce ? Math.min(18, count) : count;
  for (let i = 0; i < n; i += 1) {
    // aim mostly upward, then let gravity bring them down
    const base = up ? -Math.PI / 2 : Math.PI / 2;
    const angle = base + (Math.sin(i * 999.13) * spread) / 2;
    const speed = power * (0.55 + Math.abs(Math.cos(i * 12.9898)) * 0.75);
    particles.push({
      x: ox,
      y: oy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 6 + (i % 5),
      color: COLORS[i % COLORS.length],
      rot: i,
      spin: (i % 2 ? 1 : -1) * (0.12 + (i % 4) * 0.03),
      life: 70 + (i % 30),
    });
  }
  if (!raf) raf = requestAnimationFrame(tick);
};

if (typeof window !== "undefined") {
  window.addEventListener("resize", resize);
}

/** A widget just landed — a modest burst from where it dropped. */
export const celebrateAdd = (origin) =>
  emit({ x: origin?.x ?? 0.5, y: origin?.y ?? 0.4, count: 70, spread: Math.PI * 0.9, power: 8 });

/** The board went live — the biggest moment, a wide double burst. */
export const celebratePublish = () => {
  emit({ x: 0.32, y: 0.5, count: 90, spread: Math.PI, power: 10 });
  emit({ x: 0.68, y: 0.5, count: 90, spread: Math.PI, power: 10 });
};
